import {
  CLASSES,
  DEFAULT_UTILITY,
  UTILITY,
  defaultBar,
  defaultTalents,
  isBar,
  isTalents,
  isUtility,
  type AbilityId,
  type ClassId,
  type ClassPair,
  type Talents,
} from '@arena/shared';
import type { Shadows } from './environment';
import type { FrameId, FramePos } from './frames';
import type { Step } from './tutorial';

/** Rebindable actions by section: id, label, default keys (`KeyboardEvent.code`, or a `chord` outside Movement). */
export const BINDINGS: [string, [string, string, string[]][]][] = [
  [
    'Movement',
    [
      ['forward', 'Move forward', ['KeyW', 'ArrowUp']],
      ['back', 'Move back', ['KeyS', 'ArrowDown']],
      ['turnLeft', 'Turn left (strafe while steering)', ['KeyA']],
      ['turnRight', 'Turn right (strafe while steering)', ['KeyD']],
      ['strafeLeft', 'Strafe left', ['KeyQ']],
      ['strafeRight', 'Strafe right', ['KeyE']],
      ['jump', 'Jump', ['Space']],
    ],
  ],
  [
    'Targeting',
    [
      ['nextTarget', 'Cycle enemy targets', ['Tab']],
      ['targetSelf', 'Target self', ['F1']],
      ['targetParty1', 'Target party member 1', ['F2']],
      ['targetParty2', 'Target party member 2', ['F3']],
    ],
  ],
  [
    'Action bar',
    [
      ['swap', 'Swap class', ['Backquote']],
      ...[...'12345RFG']
        .map((c) => (c <= '9' ? `Digit${c}` : `Key${c}`))
        .map((code, i): [string, string, string[]] => [
          `action${i + 1}`,
          `Action slot ${i + 1}`,
          [code],
        ]),
    ],
  ],
  [
    'Interface',
    [
      ['spellbook', 'Spellbook', ['KeyP']],
      ['talents', 'Talents', ['KeyN']],
      ['classes', 'Class (hub only)', ['KeyK']],
      ['leaderboard', 'Leaderboard (hub only)', ['KeyL']],
      ['achievements', 'Achievements (hub only)', ['KeyO']],
      ['character', 'Character (hub only)', ['KeyC']],
    ],
  ],
];

export type CameraFollow = 'moving' | 'always' | 'never';

/** What the unit frames' health and resource bars say. */
export type StatusText = 'none' | 'number' | 'both' | 'percent';

/** Floating combat text by kind, in the order the Settings panel lists them. */
export const COMBAT_TEXT = {
  dealt: 'Damage you deal',
  taken: 'Damage you take',
  periodic: 'Periodic ticks (DoTs and HoTs)',
  healing: 'Healing',
  overheal: 'Overhealing',
  absorbs: 'Your shields absorbing damage on others',
  auras: 'Auras gained and fading on you',
  debuffs: 'CC and debuffs you land',
  dispels: 'Dispels, breaks and reveals',
  immune: 'Immune, Reflect, Grounded, cheating death',
  interrupts: 'Interrupts and broken casts',
  resources: 'Resource gains',
  resets: 'Cooldown resets',
  kills: 'Killing blows',
};
export type CombatTextKind = keyof typeof COMBAT_TEXT;

const DEFAULTS = {
  keys: Object.fromEntries(
    BINDINGS.flatMap(([, rows]) => rows).map(([id, , keys]) => [id, keys]),
  ) as Record<string, string[]>,
  /** When the camera swings back behind the character. */
  cameraFollow: 'moving' as CameraFollow,
  uiScale: 1,
  /** Share of the screen's pixel density the 3D view draws at. */
  renderScale: 1,
  antialias: true,
  shadows: 'high' as Shadows,
  /** Most frames a second the 3D view draws; 0 is as many as the screen shows. */
  fpsCap: 0,
  /** Spell particles: `low` spawns fewer of each burst. */
  effects: 'full' as 'full' | 'low',
  showFps: false,
  /** Master; each channel below scales under it. */
  volume: 0.7,
  effectsVolume: 1,
  ambienceVolume: 1,
  musicVolume: 0.15,
  interfaceVolume: 1,
  errors: true,
  combatText: true,
  combatTextKinds: Object.fromEntries(
    Object.keys(COMBAT_TEXT).map((kind) => [kind, kind !== 'overheal']),
  ) as Record<CombatTextKind, boolean>,
  lossOfControl: true,
  meter: true,
  targetOfTarget: true,
  swingTimer: true,
  statusText: 'number' as StatusText,
  /** Locks the action bars; off lets spells be dragged between slots. */
  lockBars: true,
  spellNames: false,
  bars: {} as Partial<Record<ClassId, AbilityId[]>>,
  utility: DEFAULT_UTILITY,
  talents: {} as Partial<Record<ClassId, Talents>>,
  /** Off lets the unit frames and cast bar be dragged around. */
  lockFrames: true,
  frames: {} as Partial<Record<FrameId, FramePos>>,
  /** The last classes picked, restored on connect. */
  classes: null as ClassPair | null,
  tutorial: 'class' as Step,
  /** Lowercased names of players whose chat, invites and duels we never see (/ignore). */
  ignored: [] as string[],
};

const STORAGE_KEY = 'arena-settings';

function load(): typeof DEFAULTS {
  const defaults = structuredClone(DEFAULTS);
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return {
      ...defaults,
      ...saved,
      keys: { ...defaults.keys, ...saved.keys },
      combatTextKinds: {
        ...defaults.combatTextKinds,
        ...saved.combatTextKinds,
      },
    };
  } catch {
    return defaults;
  }
}

export const settings = $state(load());

$effect.root(() => {
  $effect(() => localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)));
});

export const resetKeys = () => (settings.keys = structuredClone(DEFAULTS.keys));

/** The binding a key triggers, if any. */
export const boundTo = (code: string): string | undefined =>
  Object.keys(settings.keys).find((id) => settings.keys[id].includes(code));

/** `Shift+1`, or `s1` where space is tight. */
export function keyLabel(combo: string | undefined, short = false): string {
  if (!combo) return '';
  const mods = combo.split('+');
  const key = mods
    .pop()!
    .replace(/^(Key|Digit|Arrow)/, '')
    .replace(/^Minus$/, '-')
    .replace(/^Backquote$/, '`');
  return mods.map((m) => (short ? m[0].toLowerCase() : `${m}+`)).join('') + key;
}

/** The class's bar as the player set it up; a saved bar that no longer fits the class is ignored. */
export function barFor(classId: ClassId): AbilityId[] {
  const saved = settings.bars[classId];
  return saved && isBar(classId, saved) ? saved : defaultBar(classId);
}

/** The class's talents as the player picked them, likewise. */
export function talentsFor(classId: ClassId): Talents {
  const saved = settings.talents[classId];
  return saved && isTalents(classId, saved) ? saved : defaultTalents(classId);
}

/** Picks `id` for its slot (`TALENT_SLOTS` order). */
export function pickTalent(classId: ClassId, slot: number, id: string): void {
  const talents: string[] = [...talentsFor(classId)];
  talents[slot] = id;
  if (isTalents(classId, talents)) settings.talents[classId] = talents;
}

/** The utility slots both classes share, likewise. */
export const utilityBar = (): AbilityId[] =>
  isUtility(settings.utility) ? settings.utility : DEFAULT_UTILITY;

/** Puts a spell in a slot of a class's bar or the utility slots. A spell sits in one slot at most, so one already there trades places. */
export function placeSpell(
  row: ClassId | 'utility',
  abilityId: AbilityId,
  slot: number,
): void {
  const utility = row === 'utility';
  const bar = [...(utility ? utilityBar() : barFor(row))];
  const fits = utility ? UTILITY : CLASSES[row].abilities;
  if (slot >= bar.length || !fits.includes(abilityId)) return;
  const from = bar.indexOf(abilityId);
  if (from >= 0) bar[from] = bar[slot];
  bar[slot] = abilityId;
  if (utility) settings.utility = bar;
  else settings.bars[row] = bar;
}
